import { Solar } from 'lunar-typescript';
import { DALUREN_GUA_COMMENTS } from '../data/dalurenGuaComments';

export const DALUREN_BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;
export const DALUREN_STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
const DALUREN_TIAN_JIANGS = ['贵', '蛇', '雀', '合', '勾', '龙', '空', '虎', '常', '玄', '阴', '后'] as const;
const BAZHUAN_DAYS = new Set(['甲寅', '庚申', '丁未', '己未']);
const MENG_BRANCHES = new Set(['寅', '巳', '申', '亥']);
const ZHONG_BRANCHES = new Set(['子', '卯', '午', '酉']);

const STEM_WUXING: Record<string, string> = {
  甲: '木',
  乙: '木',
  丙: '火',
  丁: '火',
  戊: '土',
  己: '土',
  庚: '金',
  辛: '金',
  壬: '水',
  癸: '水',
};

const BRANCH_WUXING: Record<string, string> = {
  子: '水',
  丑: '土',
  寅: '木',
  卯: '木',
  辰: '土',
  巳: '火',
  午: '火',
  未: '土',
  申: '金',
  酉: '金',
  戌: '土',
  亥: '水',
};

const WUXING_SHENG: Record<string, string> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

const WUXING_KE: Record<string, string> = {
  木: '土',
  土: '水',
  水: '火',
  火: '金',
  金: '木',
};

const LIUHE_PARTNER: Record<string, BranchName> = {
  子: '丑',
  丑: '子',
  寅: '亥',
  亥: '寅',
  卯: '戌',
  戌: '卯',
  辰: '酉',
  酉: '辰',
  巳: '申',
  申: '巳',
  午: '未',
  未: '午',
};

const JI_GONG_BY_STEM: Record<string, BranchName> = {
  甲: '寅',
  乙: '辰',
  丙: '巳',
  丁: '未',
  戊: '巳',
  己: '未',
  庚: '申',
  辛: '戌',
  壬: '亥',
  癸: '丑',
};

const ZI_HOUR_STEM_START: Record<string, StemName> = {
  甲: '甲',
  己: '甲',
  乙: '丙',
  庚: '丙',
  丙: '戊',
  辛: '戊',
  丁: '庚',
  壬: '庚',
  戊: '壬',
  癸: '壬',
};

// 甲戊庚牛羊
const CLASSIC_DAY_GUI_REN_MAP: Record<string, BranchName> = {
  甲: '丑',
  乙: '子',
  丙: '亥',
  丁: '亥',
  戊: '丑',
  己: '子',
  庚: '丑',
  辛: '午',
  壬: '巳',
  癸: '巳',
};

const CLASSIC_NIGHT_GUI_REN_MAP: Record<string, BranchName> = {
  甲: '未',
  乙: '申',
  丙: '酉',
  丁: '酉',
  戊: '未',
  己: '申',
  庚: '未',
  辛: '寅',
  壬: '卯',
  癸: '卯',
};

// 甲羊戊庚牛
const ALTERNATE_DAY_GUI_REN_MAP: Record<string, BranchName> = {
  甲: '未',
  乙: '申',
  丙: '酉',
  丁: '亥',
  戊: '丑',
  己: '子',
  庚: '丑',
  辛: '寅',
  壬: '卯',
  癸: '巳',
};

const ALTERNATE_NIGHT_GUI_REN_MAP: Record<string, BranchName> = {
  甲: '丑',
  乙: '子',
  丙: '亥',
  丁: '酉',
  戊: '未',
  己: '申',
  庚: '未',
  辛: '午',
  壬: '巳',
  癸: '卯',
};

const GOOD_TIAN_JIANGS = new Set(['贵', '合', '龙', '常', '阴', '后']);

const DAY_ROW_GRID: Array<Array<BranchName | null>> = [
  ['巳', '午', '未', '申'],
  ['辰', null, null, '酉'],
  ['卯', null, null, '戌'],
  ['寅', '丑', '子', '亥'],
];

type StemName = typeof DALUREN_STEMS[number];
type BranchName = typeof DALUREN_BRANCHES[number];
type TianJiangName = typeof DALUREN_TIAN_JIANGS[number];
type YinYang = '阳' | '阴';
type WuXing = '木' | '火' | '土' | '金' | '水';

interface GanZhiValue {
  stem: StemName;
  branch: BranchName;
}

interface LiInfo {
  yearPillar: GanZhiValue;
  monthPillar: GanZhiValue;
  dayPillar: GanZhiValue;
  hourPillar: GanZhiValue;
  monthGeneral: BranchName;
  solarTermJieName: string;
  solarTermJieTime: Date;
  solarTermQiName: string;
  solarTermQiTime: Date;
  nextJieName: string;
  nextJieTime: Date;
}

interface SikeData {
  gan: StemName;
  ganYang: BranchName;
  ganYin: BranchName;
  zhi: BranchName;
  zhiYang: BranchName;
  zhiYin: BranchName;
}

interface SanchuanData {
  chu: BranchName;
  zhong: BranchName;
  mo: BranchName;
  dunGan: [string, string, string];
  liuQin: [string, string, string];
  types: string[];
}

interface TianPanData {
  map: Record<BranchName, BranchName>;
  linMap: Record<BranchName, BranchName>;
}

interface TianJiangPanData {
  reverse: boolean;
  guiRen: BranchName;
  map: Record<BranchName, TianJiangName>;
}

export type DalurenGender = 'male' | 'female';
export type DalurenGuiType = 'auto' | 'day' | 'night';
export type DalurenChangeMethod = 'zhongqi' | 'jieqi';
export type DalurenGuiRenRule = 'classic' | 'alternate';

export interface DalurenCalculateOptions {
  dateTime: Date | string;
  question?: string;
  birthYear?: number;
  gender?: DalurenGender;
  zhanShi?: BranchName;
  guiType?: DalurenGuiType;
  changeMethod?: DalurenChangeMethod;
  guiRenRule?: DalurenGuiRenRule;
}

export interface DalurenPlateCell {
  ground: BranchName;
  heaven: BranchName;
  tianJiang: TianJiangName;
}

export interface DalurenGuaComment {
  gua: string;
  content: string;
  sources: string[];
}

export interface DalurenResult {
  question: string;
  dateText: string;
  dateTimeValue: string;
  keStyle: string;
  solarTermRange: string;
  yearPillar: string;
  monthPillar: string;
  dayPillar: string;
  hourPillar: string;
  dayStem: StemName;
  dayBranch: BranchName;
  monthGeneral: BranchName;
  zhanShi: BranchName;
  dayNightLabel: string;
  birthYear: number;
  genderLabel: '男' | '女';
  benMing: string;
  xingNian: string;
  kongWang: string;
  tianPanRows: Array<Array<DalurenPlateCell | null>>;
  sikeRows: [[BranchName, BranchName, BranchName, BranchName], [BranchName, BranchName, BranchName, StemName]];
  sikeTianJiangRows: [[TianJiangName, TianJiangName, TianJiangName, TianJiangName]];
  xunDunByBranch: Record<BranchName, string>;
  sanchuanRows: [
    { relation: string; dunGan: string; branch: BranchName; tianJiang: TianJiangName },
    { relation: string; dunGan: string; branch: BranchName; tianJiang: TianJiangName },
    { relation: string; dunGan: string; branch: BranchName; tianJiang: TianJiangName }
  ];
  guaTi: string[];
  guaComments: DalurenGuaComment[];
}

class NoSanchuanError extends Error {}

const mod = (value: number, base: number): number => ((value % base) + base) % base;

const pad2 = (v: number): string => String(v).padStart(2, '0');

const formatDateTime = (date: Date): string => `${date.getFullYear()}年${pad2(date.getMonth() + 1)}月${pad2(date.getDate())}日 ${pad2(date.getHours())}时${pad2(date.getMinutes())}分`;

const formatDateTimeCompact = (date: Date): string => `${date.getFullYear()}.${pad2(date.getMonth() + 1)}.${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const toDateTimeLocalValue = (date: Date): string => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
);

const parseDateTimeValue = (input: Date | string): Date => {
  const d = input instanceof Date ? new Date(input) : new Date(String(input));
  if (!Number.isFinite(d.getTime())) {
    throw new Error('起课时间无效');
  }
  return d;
};

const isStem = (v: string): v is StemName => DALUREN_STEMS.includes(v as StemName);
const isBranch = (v: string): v is BranchName => DALUREN_BRANCHES.includes(v as BranchName);
const isTianJiang = (v: string): v is TianJiangName => DALUREN_TIAN_JIANGS.includes(v as TianJiangName);

const stemIndex = (stem: StemName): number => DALUREN_STEMS.indexOf(stem);
const branchIndex = (branch: BranchName): number => DALUREN_BRANCHES.indexOf(branch);
const tianJiangIndex = (name: TianJiangName): number => DALUREN_TIAN_JIANGS.indexOf(name);

const addStem = (stem: StemName, delta: number): StemName => DALUREN_STEMS[mod(stemIndex(stem) + delta, 10)];
const addBranch = (branch: BranchName, delta: number): BranchName => DALUREN_BRANCHES[mod(branchIndex(branch) + delta, 12)];
const addTianJiang = (name: TianJiangName, delta: number): TianJiangName => DALUREN_TIAN_JIANGS[mod(tianJiangIndex(name) + delta, 12)];

const stemDiff = (left: StemName, right: StemName): number => stemIndex(left) - stemIndex(right);
const branchDiff = (left: BranchName, right: BranchName): number => branchIndex(left) - branchIndex(right);

const stemYinYang = (stem: StemName): YinYang => (stemIndex(stem) % 2 === 0 ? '阳' : '阴');
const branchYinYang = (branch: BranchName): YinYang => (branchIndex(branch) % 2 === 0 ? '阳' : '阴');

const wuxingSheng = (from: WuXing, to: WuXing): boolean => WUXING_SHENG[from] === to;
const wuxingKe = (from: WuXing, to: WuXing): boolean => WUXING_KE[from] === to;

const wuxingOfStem = (stem: StemName): WuXing => STEM_WUXING[stem] as WuXing;
const wuxingOfBranch = (branch: BranchName): WuXing => BRANCH_WUXING[branch] as WuXing;

const wuxingOf = (value: StemName | BranchName): WuXing => (
  isStem(value) ? wuxingOfStem(value) : wuxingOfBranch(value)
);

const sameYinYang = (a: StemName | BranchName, b: StemName | BranchName): boolean => {
  const ya = isStem(a) ? stemYinYang(a) : branchYinYang(a);
  const yb = isStem(b) ? stemYinYang(b) : branchYinYang(b);
  return ya === yb;
};

const liuHe = (a: BranchName, b: BranchName): boolean => LIUHE_PARTNER[a] === b;
const liuChong = (a: BranchName, b: BranchName): boolean => mod(branchDiff(a, b), 12) === 6;

const xing = (a: BranchName, b: BranchName): boolean => {
  if (a === '子' && b === '卯') return true;
  if (a === '卯' && b === '子') return true;

  if (a === '寅' && b === '巳') return true;
  if (a === '巳' && b === '申') return true;
  if (a === '申' && b === '寅') return true;

  if (a === '丑' && b === '戌') return true;
  if (a === '戌' && b === '未') return true;
  if (a === '未' && b === '丑') return true;

  if ((a === '辰' || a === '午' || a === '酉' || a === '亥') && a === b) return true;
  return false;
};

const parseGanZhi = (text: string): GanZhiValue => {
  const stem = text.slice(0, 1);
  const branch = text.slice(1, 2);
  if (!isStem(stem) || !isBranch(branch)) {
    throw new Error(`干支格式异常: ${text}`);
  }
  return { stem, branch };
};

const addGanZhi = (gz: GanZhiValue, delta: number): GanZhiValue => ({
  stem: addStem(gz.stem, delta),
  branch: addBranch(gz.branch, delta),
});

const ganZhiToText = (gz: GanZhiValue): string => `${gz.stem}${gz.branch}`;

const jiGongOfStem = (stem: StemName): BranchName => JI_GONG_BY_STEM[stem];

const stemsOfJiGongBranch = (branch: BranchName): StemName[] => {
  const list: StemName[] = [];
  DALUREN_STEMS.forEach((stem) => {
    if (jiGongOfStem(stem) === branch) list.push(stem);
  });
  return list;
};

const getShiChenByHour = (hour: number): BranchName => {
  let idx = Math.floor((hour + 1) / 2) + 1;
  if (idx === 13) idx = 1;
  return DALUREN_BRANCHES[idx - 1];
};

const parseSolarDateTime = (text: string): Date => {
  const [datePart = '', timePart = '00:00:00'] = text.trim().split(' ');
  const [y, m, d] = datePart.split('-').map((v) => Number(v));
  const [hh, mm, ss] = timePart.split(':').map((v) => Number(v));
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0);
};

const chooseQiBetweenJie = (
  prevJieTime: Date,
  nextJieTime: Date,
  prevQiName: string,
  prevQiTime: Date,
  nextQiName: string,
  nextQiTime: Date,
): { name: string; time: Date } => {
  const inRange = (d: Date): boolean => d.getTime() >= prevJieTime.getTime() && d.getTime() <= nextJieTime.getTime();
  if (inRange(prevQiTime)) return { name: prevQiName, time: prevQiTime };
  if (inRange(nextQiTime)) return { name: nextQiName, time: nextQiTime };
  return { name: prevQiName, time: prevQiTime };
};

const getWangShuai = (monthBranch: BranchName, targetWuXing: WuXing): '旺' | '相' | '休' | '囚' | '死' => {
  const monthWuXing = wuxingOfBranch(monthBranch);
  if (wuxingSheng(monthWuXing, targetWuXing)) return '相';
  if (wuxingKe(monthWuXing, targetWuXing)) return '死';
  if (wuxingSheng(targetWuXing, monthWuXing)) return '休';
  if (wuxingKe(targetWuXing, monthWuXing)) return '囚';
  return '旺';
};

const buildLiInfo = (date: Date, changeMethod: DalurenChangeMethod): LiInfo => {
  const solar = Solar.fromYmdHms(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
  );
  const lunar = solar.getLunar();

  const yearPillar = parseGanZhi(lunar.getYearInGanZhiExact());
  const monthPillar = parseGanZhi(lunar.getMonthInGanZhiExact());
  const dayPillarRaw = parseGanZhi(lunar.getDayInGanZhiExact());

  const shiChen = getShiChenByHour(date.getHours());
  const ziStem = ZI_HOUR_STEM_START[dayPillarRaw.stem];
  const hourPillar: GanZhiValue = {
    stem: addStem(ziStem, branchDiff(shiChen, '子')),
    branch: shiChen,
  };

  // 与主流在线排盘口径（如易运盘）一致：日柱按日历日，不在子时自动进一日。
  const dayPillar = dayPillarRaw;

  const prevJie = lunar.getPrevJie(true);
  const nextJie = lunar.getNextJie(true);
  const prevQi = lunar.getPrevQi(true);
  const nextQi = lunar.getNextQi(true);

  const prevJieName = prevJie.getName();
  const prevJieTime = parseSolarDateTime(prevJie.getSolar().toYmdHms());
  const nextJieName = nextJie.getName();
  const nextJieTime = parseSolarDateTime(nextJie.getSolar().toYmdHms());

  const prevQiName = prevQi.getName();
  const prevQiTime = parseSolarDateTime(prevQi.getSolar().toYmdHms());
  const nextQiName = nextQi.getName();
  const nextQiTime = parseSolarDateTime(nextQi.getSolar().toYmdHms());

  const qiBetween = chooseQiBetweenJie(
    prevJieTime,
    nextJieTime,
    prevQiName,
    prevQiTime,
    nextQiName,
    nextQiTime,
  );

  let monthGeneral = LIUHE_PARTNER[monthPillar.branch];
  if (changeMethod === 'zhongqi' && date.getTime() < qiBetween.time.getTime()) {
    monthGeneral = addBranch(monthGeneral, 1);
  }

  return {
    yearPillar,
    monthPillar,
    dayPillar,
    hourPillar,
    monthGeneral,
    solarTermJieName: prevJieName,
    solarTermJieTime: prevJieTime,
    solarTermQiName: qiBetween.name,
    solarTermQiTime: qiBetween.time,
    nextJieName,
    nextJieTime,
  };
};

const buildTianPan = (monthGeneral: BranchName, zhanShi: BranchName): TianPanData => {
  const tianPanBase = addBranch(monthGeneral, -branchDiff(zhanShi, '子'));

  const map = {} as Record<BranchName, BranchName>;
  const linMap = {} as Record<BranchName, BranchName>;

  DALUREN_BRANCHES.forEach((branch) => {
    map[branch] = addBranch(tianPanBase, branchDiff(branch, '子'));
    linMap[branch] = addBranch(zhanShi, branchDiff(branch, monthGeneral));
  });

  return { map, linMap };
};

const getTianPanAt = (tianPan: TianPanData, branch: BranchName): BranchName => tianPan.map[branch];
const getTianPanLin = (tianPan: TianPanData, branch: BranchName): BranchName => tianPan.linMap[branch];

const buildSike = (tianPan: TianPanData, dayPillar: GanZhiValue): SikeData => {
  const gan = dayPillar.stem;
  const ganYang = getTianPanAt(tianPan, jiGongOfStem(gan));
  const ganYin = getTianPanAt(tianPan, ganYang);

  const zhi = dayPillar.branch;
  const zhiYang = getTianPanAt(tianPan, zhi);
  const zhiYin = getTianPanAt(tianPan, zhiYang);

  return {
    gan,
    ganYang,
    ganYin,
    zhi,
    zhiYang,
    zhiYin,
  };
};

const hasZei = (sike: SikeData): Array<[BranchName, StemName | BranchName]> => {
  const lessons: Array<[BranchName, StemName | BranchName]> = [
    [sike.ganYang, sike.gan],
    [sike.ganYin, sike.ganYang],
    [sike.zhiYang, sike.zhi],
    [sike.zhiYin, sike.zhiYang],
  ];

  const hits = lessons.filter(([upper, lower]) => wuxingKe(wuxingOf(lower), wuxingOfBranch(upper)));
  const seen = new Set<BranchName>();
  const out: Array<[BranchName, StemName | BranchName]> = [];
  hits.forEach((item) => {
    if (seen.has(item[0])) return;
    seen.add(item[0]);
    out.push(item);
  });
  return out;
};

const hasKe = (sike: SikeData): Array<[BranchName, StemName | BranchName]> => {
  const lessons: Array<[BranchName, StemName | BranchName]> = [
    [sike.ganYang, sike.gan],
    [sike.ganYin, sike.ganYang],
    [sike.zhiYang, sike.zhi],
    [sike.zhiYin, sike.zhiYang],
  ];

  const hits = lessons.filter(([upper, lower]) => wuxingKe(wuxingOfBranch(upper), wuxingOf(lower)));
  const seen = new Set<BranchName>();
  const out: Array<[BranchName, StemName | BranchName]> = [];
  hits.forEach((item) => {
    if (seen.has(item[0])) return;
    seen.add(item[0]);
    out.push(item);
  });
  return out;
};

const makeChuan = (
  chu: BranchName,
  tianPan: TianPanData,
): [BranchName, BranchName, BranchName] => {
  const zhong = getTianPanAt(tianPan, chu);
  const mo = getTianPanAt(tianPan, zhong);
  return [chu, zhong, mo];
};

const sheHai = (
  list: Array<[BranchName, StemName | BranchName]>,
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const depthList = list.map((lesson) => {
    const [upper, lower] = lesson;
    const lin = getTianPanLin(tianPan, upper);
    let count = 0;

    for (let i = 0; i < 12; i += 1) {
      const d = addBranch(lin, i);
      if (d === upper) break;
      const ganList = stemsOfJiGongBranch(d);
      const isZei = wuxingKe(wuxingOf(lower), wuxingOfBranch(upper));
      if (isZei) {
        if (wuxingKe(wuxingOfBranch(d), wuxingOfBranch(upper))) count += 1;
        ganList.forEach((g) => {
          if (wuxingKe(wuxingOfStem(g), wuxingOfBranch(upper))) count += 1;
        });
      } else {
        if (wuxingKe(wuxingOfBranch(upper), wuxingOfBranch(d))) count += 1;
        ganList.forEach((g) => {
          if (wuxingKe(wuxingOfBranch(upper), wuxingOfStem(g))) count += 1;
        });
      }
    }

    return { lesson, depth: count };
  });

  const maxDepth = depthList.reduce((max, item) => Math.max(max, item.depth), 0);
  const maxLessons = depthList.filter((item) => item.depth === maxDepth).map((item) => item.lesson);

  if (maxLessons.length === 1) {
    types.push('涉害卦');
    return makeChuan(maxLessons[0][0], tianPan);
  }

  for (const lesson of maxLessons) {
    const lin = getTianPanLin(tianPan, lesson[0]);
    if (MENG_BRANCHES.has(lin)) {
      types.push('见机卦');
      return makeChuan(lesson[0], tianPan);
    }
  }

  for (const lesson of maxLessons) {
    const lin = getTianPanLin(tianPan, lesson[0]);
    if (ZHONG_BRANCHES.has(lin)) {
      types.push('察微卦');
      return makeChuan(lesson[0], tianPan);
    }
  }

  if (stemYinYang(sike.gan) === '阳') {
    types.push('复等卦');
    return makeChuan(sike.ganYang, tianPan);
  }

  types.push('复等卦');
  return makeChuan(sike.zhiYang, tianPan);
};

const biYong = (
  list: Array<[BranchName, StemName | BranchName]>,
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const same = list.filter(([upper]) => sameYinYang(upper, sike.gan));
  if (same.length === 1) {
    types.push('知一卦');
    return makeChuan(same[0][0], tianPan);
  }
  if (same.length === 0) {
    return sheHai(list, sike, tianPan, types);
  }
  return sheHai(same, sike, tianPan, types);
};

const zeiKe = (
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const zei = hasZei(sike);
  if (zei.length > 0) {
    if (zei.length === 1) {
      types.push('重审卦');
      return makeChuan(zei[0][0], tianPan);
    }
    return biYong(zei, sike, tianPan, types);
  }

  const ke = hasKe(sike);
  if (ke.length > 0) {
    if (ke.length === 1) {
      types.push('元首卦');
      return makeChuan(ke[0][0], tianPan);
    }
    return biYong(ke, sike, tianPan, types);
  }

  throw new NoSanchuanError('不能用贼克取三传');
};

const yaoKe = (
  sike: SikeData,
  dayPillar: GanZhiValue,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  if (BAZHUAN_DAYS.has(ganZhiToText(dayPillar))) {
    throw new NoSanchuanError('八专日不用遥克');
  }

  const cands: Array<[BranchName, StemName | BranchName]> = [];

  const checkList: Array<[BranchName, StemName | BranchName]> = [
    [sike.ganYin, sike.ganYang],
    [sike.zhiYang, sike.zhi],
    [sike.zhiYin, sike.zhiYang],
  ];

  checkList.forEach(([upper, lower]) => {
    if (wuxingKe(wuxingOfBranch(upper), wuxingOfStem(sike.gan))) cands.push([upper, lower]);
  });

  if (cands.length === 0) {
    checkList.forEach(([upper, lower]) => {
      if (wuxingKe(wuxingOfStem(sike.gan), wuxingOfBranch(upper))) cands.push([upper, lower]);
    });
  }

  if (cands.length === 0) {
    throw new NoSanchuanError('无遥克，不能用遥克取三传');
  }

  const seen = new Set<BranchName>();
  const dedup = cands.filter(([upper]) => {
    if (seen.has(upper)) return false;
    seen.add(upper);
    return true;
  });

  if (dedup.length === 1) {
    types.push('遥克卦');
    return makeChuan(dedup[0][0], tianPan);
  }

  types.push('遥克卦');
  return biYong(dedup, sike, tianPan, types);
};

const angXing = (
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const seen = new Set<BranchName>();
  const lessons = [sike.ganYang, sike.ganYin, sike.zhiYang, sike.zhiYin];
  lessons.forEach((item) => seen.add(item));
  if (seen.size !== 4) {
    throw new NoSanchuanError('课不备，不能用昂星取三传');
  }

  if (stemYinYang(sike.gan) === '阳') {
    types.push('虎视卦');
    return [getTianPanAt(tianPan, '酉'), sike.zhiYang, sike.ganYang];
  }

  types.push('冬蛇掩目');
  return [getTianPanLin(tianPan, '酉'), sike.ganYang, sike.zhiYang];
};

const bieZe = (
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const lessons = [sike.ganYang, sike.ganYin, sike.zhiYang, sike.zhiYin];
  const uniq = Array.from(new Set(lessons));
  if (uniq.length === 4) {
    throw new NoSanchuanError('四课全备，不能用别责取三传');
  }
  if (uniq.length !== 3) {
    throw new NoSanchuanError('别责仅用于三课备');
  }

  const chu = stemYinYang(sike.gan) === '阳'
    ? getTianPanAt(tianPan, jiGongOfStem(addStem(sike.gan, 5)))
    : addBranch(sike.zhi, 4);

  types.push('别责卦');
  return [chu, sike.ganYang, sike.ganYang];
};

const baZhuan = (
  sike: SikeData,
  dayPillar: GanZhiValue,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  if (!BAZHUAN_DAYS.has(ganZhiToText(dayPillar))) {
    throw new NoSanchuanError('不是八专日');
  }

  const chu = stemYinYang(sike.gan) === '阳'
    ? addBranch(sike.ganYang, 2)
    : addBranch(sike.zhiYin, -2);

  types.push('八专卦');
  return [chu, sike.ganYang, sike.ganYang];
};

const fuYin = (
  sike: SikeData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  const yangDay = stemYinYang(sike.gan) === '阳';
  const isYiGui = sike.gan === '乙' || sike.gan === '癸';

  let chu: BranchName = (isYiGui || yangDay) ? sike.ganYang : sike.zhiYang;

  let zhong: BranchName = chu;
  for (const b of DALUREN_BRANCHES) {
    if (xing(chu, b)) {
      zhong = b;
      break;
    }
  }

  if (chu === zhong) {
    zhong = (isYiGui || yangDay) ? sike.zhiYang : sike.ganYang;
  }

  let mo: BranchName = zhong;
  for (const b of DALUREN_BRANCHES) {
    if (xing(zhong, b)) {
      mo = b;
      break;
    }
  }

  if (zhong === mo) {
    mo = addBranch(zhong, 6);
  }

  if (xing(zhong, chu)) {
    mo = addBranch(zhong, 6);
  }

  types.push(yangDay ? '自任卦' : '自信卦');
  return [chu, zhong, mo];
};

const fanYin = (
  sike: SikeData,
  tianPan: TianPanData,
  types: string[],
): [BranchName, BranchName, BranchName] => {
  try {
    return zeiKe(sike, tianPan, types);
  } catch {
    let zhi: BranchName = sike.zhi;
    let yiMa: BranchName = zhi;
    for (let i = 0; i < 3; i += 1) {
      if (MENG_BRANCHES.has(zhi)) {
        yiMa = addBranch(zhi, 6);
        break;
      }
      zhi = addBranch(zhi, 4);
    }

    types.push('无依卦');
    return [yiMa, sike.zhiYang, sike.ganYang];
  }
};

const buildSanchuan = (tianPan: TianPanData, sike: SikeData, dayPillar: GanZhiValue): SanchuanData => {
  const types: string[] = [];

  let chu: BranchName;
  let zhong: BranchName;
  let mo: BranchName;

  if (sike.zhiYang === sike.zhi) {
    [chu, zhong, mo] = fuYin(sike, types);
  } else if (liuChong(sike.zhiYang, sike.zhi)) {
    [chu, zhong, mo] = fanYin(sike, tianPan, types);
  } else {
    try {
      [chu, zhong, mo] = zeiKe(sike, tianPan, types);
    } catch {
      try {
        [chu, zhong, mo] = yaoKe(sike, dayPillar, tianPan, types);
      } catch {
        try {
          [chu, zhong, mo] = angXing(sike, tianPan, types);
        } catch {
          try {
            [chu, zhong, mo] = bieZe(sike, tianPan, types);
          } catch {
            [chu, zhong, mo] = baZhuan(sike, dayPillar, types);
          }
        }
      }
    }
  }

  // 与易运盘连续局对照时，涉害特例在“干阴=支、支阴=干阳”结构下
  // 初传应从支阳起传，避免出现同局仅三传顺序偏一位的问题。
  if (
    types.includes('涉害卦')
    && sike.ganYin === sike.zhi
    && sike.zhiYin === sike.ganYang
    && chu === sike.ganYang
  ) {
    [chu, zhong, mo] = makeChuan(sike.zhiYang, tianPan);
  }

  const delta = stemDiff(dayPillar.stem, '甲');
  const xunShou = addBranch(dayPillar.branch, -delta);

  const chuan = [chu, zhong, mo] as const;
  const dunGan = chuan.map((item) => {
    const d = mod(branchDiff(item, xunShou), 12);
    if (d === 10 || d === 11) return '';
    return addStem('甲', d);
  }) as [string, string, string];

  const liuQin = chuan.map((item) => {
    const dayWuXing = wuxingOfStem(dayPillar.stem);
    const itemWuXing = wuxingOfBranch(item);
    if (wuxingKe(dayWuXing, itemWuXing)) return '财';
    if (wuxingKe(itemWuXing, dayWuXing)) return '官';
    if (wuxingSheng(dayWuXing, itemWuXing)) return '子';
    if (wuxingSheng(itemWuXing, dayWuXing)) return '父';
    return '兄';
  }) as [string, string, string];

  return {
    chu,
    zhong,
    mo,
    dunGan,
    liuQin,
    types,
  };
};

const buildTianJiangMapByRule = (rule: DalurenGuiRenRule): { day: Record<StemName, BranchName>; night: Record<StemName, BranchName> } => {
  if (rule === 'alternate') {
    return {
      day: { ...ALTERNATE_DAY_GUI_REN_MAP } as Record<StemName, BranchName>,
      night: { ...ALTERNATE_NIGHT_GUI_REN_MAP } as Record<StemName, BranchName>,
    };
  }
  return {
    day: { ...CLASSIC_DAY_GUI_REN_MAP } as Record<StemName, BranchName>,
    night: { ...CLASSIC_NIGHT_GUI_REN_MAP } as Record<StemName, BranchName>,
  };
};

const buildTianJiangPan = (
  tianPan: TianPanData,
  sike: SikeData,
  dayMode: boolean,
  rule: DalurenGuiRenRule,
): TianJiangPanData => {
  const maps = buildTianJiangMapByRule(rule);
  const guiRen = dayMode ? maps.day[sike.gan] : maps.night[sike.gan];
  const guiRenDiPan = addBranch('子', branchDiff(guiRen, getTianPanAt(tianPan, '子')));
  const reverse = branchDiff(guiRenDiPan, '巳') >= 0 && branchDiff('戌', guiRenDiPan) >= 0;

  const map = {} as Record<BranchName, TianJiangName>;
  DALUREN_BRANCHES.forEach((branch) => {
    map[branch] = reverse
      ? addTianJiang('贵', branchDiff(guiRen, branch))
      : addTianJiang('贵', branchDiff(branch, guiRen));
  });

  return {
    reverse,
    guiRen,
    map,
  };
};

const getTianJiangAt = (tjp: TianJiangPanData, branch: BranchName): TianJiangName => tjp.map[branch];

const tianJiangLin = (tjp: TianJiangPanData, tj: TianJiangName): BranchName => {
  if (tjp.reverse) {
    return addBranch(tjp.guiRen, -tianJiangIndex(tj));
  }
  return addBranch(tjp.guiRen, tianJiangIndex(tj));
};

const benMingAndXingNian = (
  queryYear: number,
  birthYear: number,
  gender: DalurenGender,
): { benMing: GanZhiValue; xingNian: GanZhiValue } => {
  const birthSolar = Solar.fromYmdHms(birthYear, 5, 20, 12, 0, 0);
  const birthYearPillar = parseGanZhi(birthSolar.getLunar().getYearInGanZhiExact());

  const xingNian = gender === 'male'
    ? addGanZhi({ stem: '丙', branch: '寅' }, queryYear - birthYear)
    : addGanZhi({ stem: '壬', branch: '申' }, birthYear - queryYear);

  return {
    benMing: birthYearPillar,
    xingNian,
  };
};

const stripHtml = (raw: string): string => String(raw || '')
  .replace(/<\s*br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/\n{2,}/g, '\n')
  .trim();

const extractBookSources = (raw: string): string[] => {
  const text = String(raw || '');
  const seen = new Set<string>();
  const list: string[] = [];

  const add = (name: string): void => {
    const normalized = name.trim().replace(/[“”"']/g, '');
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    list.push(normalized);
  };

  const quoted = /《([^》\n]{1,30})》/g;
  let m: RegExpExecArray | null = quoted.exec(text);
  while (m) {
    add(m[1]);
    m = quoted.exec(text);
  }

  [
    '神定经',
    '六壬大全',
    '六壬统宗',
    '六壬统宗大全',
    '心镜',
    '心镜经',
    '心照经',
    '心照',
    '灵辖经',
    '曾门经',
    '玉衡经',
    '大六壬灵觉经',
    '灵觉经',
    '六壬大合',
  ].forEach((name) => {
    if (text.includes(name)) add(name);
  });

  return list;
};

const appendGuaTi = (list: string[], value: string): void => {
  if (!list.includes(value)) list.push(value);
};

const runGuaTiExtensions = (
  guaTi: string[],
  liInfo: LiInfo,
  tianPan: TianPanData,
  sike: SikeData,
  sanchuan: SanchuanData,
  tianJiangPan: TianJiangPanData,
  benMing: GanZhiValue,
  xingNian: GanZhiValue,
): void => {
  const sc = [sanchuan.chu, sanchuan.zhong, sanchuan.mo] as BranchName[];
  const monthJian = liInfo.monthPillar.branch;
  const dayGan = sike.gan;

  if (sike.zhi === sike.zhiYang) appendGuaTi(guaTi, '伏呤卦');
  if (addBranch(sike.zhi, 6) === sike.zhiYang) appendGuaTi(guaTi, '返呤卦');

  if (sc[0] === liInfo.monthGeneral && sc.includes(liInfo.yearPillar.branch)) {
    appendGuaTi(guaTi, '龙德卦');
  }

  const chuWuXing = wuxingOfBranch(sc[0]);
  if (['旺', '相'].includes(getWangShuai(monthJian, chuWuXing))) {
    if (
      ['旺', '相'].includes(getWangShuai(monthJian, wuxingOfStem(sike.gan)))
      && ['旺', '相'].includes(getWangShuai(monthJian, wuxingOfBranch(sike.zhi)))
    ) {
      const c = getTianJiangAt(tianJiangPan, sc[0]);
      const z = getTianJiangAt(tianJiangPan, sc[1]);
      const m = getTianJiangAt(tianJiangPan, sc[2]);
      if (GOOD_TIAN_JIANGS.has(c) || GOOD_TIAN_JIANGS.has(z) || GOOD_TIAN_JIANGS.has(m)) {
        appendGuaTi(guaTi, '三光卦');
      }
    }
  }

  if (
    ['旺', '相'].includes(getWangShuai(monthJian, chuWuXing))
    && !tianJiangPan.reverse
  ) {
    const ganTj = getTianJiangAt(tianJiangPan, jiGongOfStem(sike.gan));
    const zhiTj = getTianJiangAt(tianJiangPan, sike.zhi);
    if (['蛇', '雀', '合', '勾', '龙'].includes(ganTj) && ['蛇', '雀', '合', '勾', '龙'].includes(zhiTj)) {
      appendGuaTi(guaTi, '三阳卦');
    }
  }

  const kongA = calcKongWang(liInfo.dayPillar)[1];
  let xunQi: BranchName = '丑';
  if (kongA === '戌' || kongA === '子') xunQi = '丑';
  if (kongA === '申' || kongA === '午') xunQi = '子';
  if (kongA === '寅' || kongA === '辰') xunQi = '亥';
  if (sc.includes(xunQi)) appendGuaTi(guaTi, '三奇卦');
  if (sc.includes(kongA)) appendGuaTi(guaTi, '六仪卦');

  if (getTianPanAt(tianPan, '巳') === '戌' && sc.includes('巳') && sc.includes('戌') && sc.includes('卯')) {
    appendGuaTi(guaTi, '铸印卦');
  }

  if (sc[0] === '卯') {
    if (sike.ganYang === '卯' && (dayGan === '庚' || dayGan === '辛')) {
      appendGuaTi(guaTi, '斫轮卦');
    } else {
      const lin = getTianPanLin(tianPan, '卯');
      if (lin === '申' || lin === '酉') appendGuaTi(guaTi, '斫轮卦');
    }
  }

  {
    const tianMa = addBranch('午', branchDiff(monthJian, '寅') * 2);
    if (sc.includes('子') && sc.includes('卯') && sc.includes(tianMa)) {
      appendGuaTi(guaTi, '轩盖卦');
    }
  }

  if (sc.includes('戌')) {
    const taiChang = tianJiangLin(tianJiangPan, '常');
    if (sc.includes(taiChang)) {
      const zhiList: BranchName[] = [liInfo.yearPillar.branch, monthJian, sike.zhi, xingNian.branch, benMing.branch];
      for (const rawZhi of zhiList) {
        let zhi = rawZhi;
        while (mod(branchDiff(zhi, '寅'), 3) !== 0) {
          zhi = addBranch(zhi, 4);
        }
        const yiMa = addBranch(zhi, 6);
        if (sc.includes(yiMa)) {
          appendGuaTi(guaTi, '官爵卦');
          break;
        }
      }
    }
  }

  if (['旺', '相'].includes(getWangShuai(monthJian, wuxingOfBranch(sc[0])))) {
    const tianYi = tianJiangLin(tianJiangPan, '贵');
    if (tianYi === sc[0]) {
      const xingNianShang = getTianPanAt(tianPan, xingNian.branch);
      const benMingShang = getTianPanAt(tianPan, benMing.branch);
      if ([xingNianShang, benMingShang, sike.ganYang, sike.zhiYang].includes(tianYi)) {
        appendGuaTi(guaTi, '富贵卦');
      }
    }
  }

  if (sc[0] === liInfo.yearPillar.branch) {
    const qingLong = tianJiangLin(tianJiangPan, '龙');
    if (qingLong === monthJian && sc.includes(monthJian)) {
      appendGuaTi(guaTi, '时泰卦');
    }
  }

  if (
    ['乙', '戊', '己', '辛', '壬'].includes(dayGan)
    && ['子', '卯', '午', '酉'].includes(sike.zhi)
    && sike.zhiYang === '丑'
  ) {
    appendGuaTi(guaTi, '九丑卦');
  }

  {
    const zhi = monthJian;
    let aligned = zhi;
    while (mod(branchDiff(aligned, '寅'), 3) !== 0) {
      aligned = addBranch(aligned, 4);
    }
    const tianGui = addBranch(aligned, 7);
    const xingNianShang = getTianPanAt(tianPan, xingNian.branch);
    const benMingShang = getTianPanAt(tianPan, benMing.branch);
    if ([sike.ganYang, sike.zhiYang, sc[0], xingNianShang, benMingShang].includes(tianGui)) {
      appendGuaTi(guaTi, '伏殃卦');
    }
  }

  {
    const lessons: Array<[BranchName, StemName | BranchName]> = [
      [sike.ganYang, sike.gan],
      [sike.ganYin, sike.ganYang],
      [sike.zhiYang, sike.zhi],
      [sike.zhiYin, sike.zhiYang],
    ];
    let z = 0;
    let y = 0;
    lessons.forEach(([upper, lower]) => {
      if (wuxingKe(wuxingOfBranch(upper), wuxingOf(lower))) z += 1;
      if (wuxingKe(wuxingOf(lower), wuxingOfBranch(upper))) y += 1;
    });
    if (z === 3 || y === 3) appendGuaTi(guaTi, '度厄卦');
  }

  {
    const c1 = wuxingKe(wuxingOfBranch(sike.ganYang), wuxingOfStem(sike.gan))
      && wuxingKe(wuxingOfBranch(sike.ganYin), wuxingOfBranch(sike.ganYang))
      && wuxingKe(wuxingOfBranch(sike.zhiYang), wuxingOfBranch(sike.zhi))
      && wuxingKe(wuxingOfBranch(sike.zhiYin), wuxingOfBranch(sike.zhiYang));

    const c2 = wuxingKe(wuxingOfStem(sike.gan), wuxingOfBranch(sike.ganYang))
      && wuxingKe(wuxingOfBranch(sike.ganYang), wuxingOfBranch(sike.ganYin))
      && wuxingKe(wuxingOfBranch(sike.zhi), wuxingOfBranch(sike.zhiYang))
      && wuxingKe(wuxingOfBranch(sike.zhiYang), wuxingOfBranch(sike.zhiYin));

    if (c1 || c2) appendGuaTi(guaTi, '无禄绝嗣卦');
  }

  {
    const xingNianShang = getTianPanAt(tianPan, xingNian.branch);
    const benMingShang = getTianPanAt(tianPan, benMing.branch);
    const tianLuo = addBranch(jiGongOfStem(sike.gan), 1);
    const diWang = addBranch(tianLuo, 6);
    if ([sike.ganYang, sike.zhiYang, sc[0], xingNianShang, benMingShang].includes(tianLuo)
      || [sike.ganYang, sike.zhiYang, sc[0], xingNianShang, benMingShang].includes(diWang)) {
      appendGuaTi(guaTi, '罗网卦');
    }
  }

  {
    const gYang = getTianJiangAt(tianJiangPan, sike.ganYang);
    const gYin = getTianJiangAt(tianJiangPan, sike.ganYin);
    const zYang = getTianJiangAt(tianJiangPan, sike.zhiYang);
    const zYin = getTianJiangAt(tianJiangPan, sike.zhiYin);
    const target = new Set(['蛇', '雀', '合', '勾', '龙']);
    if (!target.has(gYang) && !target.has(zYang) && target.has(gYin) && target.has(zYin)) {
      appendGuaTi(guaTi, '微服卦');
    }
  }

  {
    const [c, z, m] = sc;
    if ((mod(branchDiff(c, '寅'), 3) === 0 && addBranch(c, 1) === z && addBranch(z, 1) === m)
      || (mod(branchDiff(c, '辰'), 3) === 0 && c === addBranch(z, 1) && z === addBranch(m, 1))
      || (liInfo.yearPillar.branch === c && liInfo.monthPillar.branch === z && liInfo.dayPillar.branch === m)
      || (liInfo.dayPillar.branch === c && liInfo.monthPillar.branch === z && liInfo.yearPillar.branch === m)
    ) {
      appendGuaTi(guaTi, '连珠卦');
    }
  }

  {
    const [c, z, m] = sc;
    if ((addBranch(c, 1) === z && addBranch(z, 1) === m)
      || (c === addBranch(z, 1) && z === addBranch(m, 1))) {
      appendGuaTi(guaTi, '连茹卦');
    }
  }
};

const calcKongWang = (dayPillar: GanZhiValue): [BranchName, BranchName] => {
  const delta = stemDiff(dayPillar.stem, '甲');
  const xunShou = addBranch(dayPillar.branch, -delta);
  return [addBranch(xunShou, -2), addBranch(xunShou, -1)];
};

const buildXunDunByBranch = (dayPillar: GanZhiValue): Record<BranchName, string> => {
  const out = {} as Record<BranchName, string>;
  const delta = stemDiff(dayPillar.stem, '甲');
  const xunShou = addBranch(dayPillar.branch, -delta);
  DALUREN_BRANCHES.forEach((branch) => {
    const zhiDelta = mod(branchDiff(branch, xunShou), 12);
    if (zhiDelta === 10 || zhiDelta === 11) {
      out[branch] = '○';
      return;
    }
    out[branch] = addStem('甲', zhiDelta);
  });
  return out;
};

const buildHourPillarByZhanShi = (dayStem: StemName, zhanShi: BranchName): GanZhiValue => {
  const ziHourStem = ZI_HOUR_STEM_START[dayStem];
  return {
    stem: addStem(ziHourStem, branchDiff(zhanShi, '子')),
    branch: zhanShi,
  };
};

const toBranchFromRaw = (value?: string | null): BranchName | null => {
  if (!value) return null;
  const b = String(value).trim().slice(0, 1);
  return isBranch(b) ? b : null;
};

const autoDayModeByZhanShi = (_date: Date, zhanShi: BranchName, _monthBranch: BranchName): boolean => {
  // 卯酉区分（常用口径）：卯至申为昼，酉至寅为夜。
  return ['卯', '辰', '巳', '午', '未', '申'].includes(zhanShi);
};

const buildGuaComments = (guaTi: string[]): DalurenGuaComment[] => {
  const comments: DalurenGuaComment[] = [];
  guaTi.forEach((gua) => {
    const raw = DALUREN_GUA_COMMENTS[gua] || DALUREN_GUA_COMMENTS[gua.replace(/课$/, '卦')] || '';
    if (!raw) return;
    comments.push({
      gua,
      content: stripHtml(raw),
      sources: extractBookSources(raw),
    });
  });
  return comments;
};

export function getDalurenShiChenOptions(): Array<{ branch: BranchName; label: string }> {
  return DALUREN_BRANCHES.map((branch) => ({ branch, label: `${branch}时` }));
}

export function getShiChenByDateTime(input: Date | string): BranchName {
  const d = parseDateTimeValue(input);
  return getShiChenByHour(d.getHours());
}

export function calcDaluren(options: DalurenCalculateOptions): DalurenResult {
  const date = parseDateTimeValue(options.dateTime);
  const question = String(options.question || '').trim();
  const birthYear = Number.isFinite(Number(options.birthYear)) ? Number(options.birthYear) : 1990;
  const gender: DalurenGender = options.gender === 'female' ? 'female' : 'male';
  const guiType: DalurenGuiType = options.guiType || 'auto';
  const changeMethod: DalurenChangeMethod = options.changeMethod === 'jieqi' ? 'jieqi' : 'zhongqi';
  const guiRenRule: DalurenGuiRenRule = options.guiRenRule === 'alternate' ? 'alternate' : 'classic';

  const li = buildLiInfo(date, changeMethod);

  const inputZhanShi = toBranchFromRaw(options.zhanShi);
  const zhanShi = inputZhanShi || getShiChenByHour(date.getHours());
  const hourPillar = inputZhanShi ? buildHourPillarByZhanShi(li.dayPillar.stem, zhanShi) : li.hourPillar;
  const dayMode = guiType === 'day' ? true : guiType === 'night' ? false : autoDayModeByZhanShi(date, zhanShi, li.monthPillar.branch);

  const tianPan = buildTianPan(li.monthGeneral, zhanShi);
  const sike = buildSike(tianPan, li.dayPillar);
  const sanchuan = buildSanchuan(tianPan, sike, li.dayPillar);
  const tianJiangPan = buildTianJiangPan(tianPan, sike, dayMode, guiRenRule);

  const { benMing, xingNian } = benMingAndXingNian(date.getFullYear(), birthYear, gender);

  const guaTi = [...sanchuan.types];
  runGuaTiExtensions(guaTi, li, tianPan, sike, sanchuan, tianJiangPan, benMing, xingNian);

  if (guaTi.includes('虎视卦') || guaTi.includes('冬蛇掩目')) appendGuaTi(guaTi, '虎视卦');
  if (guaTi.includes('见机卦') || guaTi.includes('察微卦') || guaTi.includes('复等卦')) appendGuaTi(guaTi, '涉害卦');

  const [kongA, kongB] = calcKongWang(li.dayPillar);
  const xunDunByBranch = buildXunDunByBranch(li.dayPillar);

  const tianPanRows = DAY_ROW_GRID.map((row) => row.map((ground) => {
    if (!ground) return null;
    const heaven = getTianPanAt(tianPan, ground);
    const tianJiang = getTianJiangAt(tianJiangPan, heaven);
    return { ground, heaven, tianJiang };
  }));

  const sikeRows: [[BranchName, BranchName, BranchName, BranchName], [BranchName, BranchName, BranchName, StemName]] = [
    [sike.zhiYin, sike.zhiYang, sike.ganYin, sike.ganYang],
    [sike.zhiYang, sike.zhi, sike.ganYang, sike.gan],
  ];

  const sikeTianJiangRows: [[TianJiangName, TianJiangName, TianJiangName, TianJiangName]] = [[
    getTianJiangAt(tianJiangPan, sike.zhiYin),
    getTianJiangAt(tianJiangPan, sike.zhiYang),
    getTianJiangAt(tianJiangPan, sike.ganYin),
    getTianJiangAt(tianJiangPan, sike.ganYang),
  ]];

  const sanchuanBranches = [sanchuan.chu, sanchuan.zhong, sanchuan.mo] as const;

  const sanchuanRows: DalurenResult['sanchuanRows'] = [0, 1, 2].map((idx) => ({
    relation: sanchuan.liuQin[idx],
    dunGan: sanchuan.dunGan[idx],
    branch: sanchuanBranches[idx],
    tianJiang: getTianJiangAt(tianJiangPan, sanchuanBranches[idx]),
  })) as DalurenResult['sanchuanRows'];

  const guaComments = buildGuaComments(guaTi);

  const solarRange = `${li.solarTermQiName}${formatDateTimeCompact(li.solarTermQiTime)} ~ ${li.nextJieName}${formatDateTimeCompact(li.nextJieTime)}`;

  return {
    question,
    dateText: formatDateTime(date),
    dateTimeValue: toDateTimeLocalValue(date),
    keStyle: `${zhanShi}时起课`,
    solarTermRange: solarRange,
    yearPillar: ganZhiToText(li.yearPillar),
    monthPillar: ganZhiToText(li.monthPillar),
    dayPillar: ganZhiToText(li.dayPillar),
    hourPillar: ganZhiToText(hourPillar),
    dayStem: li.dayPillar.stem,
    dayBranch: li.dayPillar.branch,
    monthGeneral: li.monthGeneral,
    zhanShi,
    dayNightLabel: dayMode ? '昼占' : '夜占',
    birthYear,
    genderLabel: gender === 'male' ? '男' : '女',
    benMing: ganZhiToText(benMing),
    xingNian: ganZhiToText(xingNian),
    kongWang: `${kongA}${kongB}`,
    tianPanRows,
    sikeRows,
    sikeTianJiangRows,
    xunDunByBranch,
    sanchuanRows,
    guaTi,
    guaComments,
  };
}
